// IdProvider — mobile-safe UUID generation.
//
// crypto.randomUUID() is unavailable in some mobile Safari / WKWebView contexts
// (and only on secure origins), which is exactly the Capacitor environment this
// app ships into. We try it, then fall back to a crypto.getRandomValues-based
// RFC-4122 v4 generator, and finally to Math.random as a last resort.

import type { IdProvider } from '../bridge/ports';

function fromRandomValues(): string | null {
  const c = (globalThis as { crypto?: Crypto }).crypto;
  if (!c || typeof c.getRandomValues !== 'function') return null;
  const bytes = new Uint8Array(16);
  c.getRandomValues(bytes);
  // Per RFC 4122 §4.4: set version (4) and variant (10xx) bits.
  bytes[6] = (bytes[6]! & 0x0f) | 0x40;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0'));
  return (
    hex.slice(0, 4).join('') +
    '-' +
    hex.slice(4, 6).join('') +
    '-' +
    hex.slice(6, 8).join('') +
    '-' +
    hex.slice(8, 10).join('') +
    '-' +
    hex.slice(10, 16).join('')
  );
}

function fromMathRandom(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
    const r = (Math.random() * 16) | 0;
    const v = ch === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export function generateId(): string {
  const c = (globalThis as { crypto?: Crypto }).crypto;
  if (c && typeof c.randomUUID === 'function') {
    try {
      return c.randomUUID();
    } catch {
      // fall through
    }
  }
  return fromRandomValues() ?? fromMathRandom();
}

export class WebIdProvider implements IdProvider {
  newId(): string {
    return generateId();
  }
}
